import { z } from 'zod';
import { id } from './primitives';

/**
 * Event types on the single stream.
 *
 * These are facts about what a reader did, not commands and not aggregates. Adding a format must
 * not add an event type: a personality quiz and a trivia quiz both emit `answer` and `complete`,
 * and it is the interaction config that gives those events meaning.
 */
export const EVENT_TYPES = [
  /** No longer emitted since 6 September 2026, kept so the archive stays readable. See below. */
  'impression',
  'start',
  'answer',
  'complete',
  'submit',
  'identify',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/**
 * The widget was rendered and became visible to the reader.
 *
 * NO LONGER EMITTED, since 6 September 2026. The widget used to fire this from an
 * IntersectionObserver, which at 500 million monthly page views was 99.6 per cent of everything on
 * the stream and about three quarters of the infrastructure bill, for the one event that recorded
 * nothing a reader had done. Views are counted from the CDN access logs instead (D22 and D23).
 *
 * The type stays because the archive holds years of these and a reader that cannot parse its own
 * history is not an archive: `services/archiver/src/replay.ts` has to be able to read every row it
 * ever wrote. Nothing emits it, nothing projects it, and adding an emitter would be a mistake
 * rather than a feature. `visible_ms` is the field that has no successor: there is no viewability
 * signal now, and a log line cannot tell "seen" from "loaded".
 */
export const impressionData = z
  .object({
    visible_ms: z.number().int().min(0).max(86_400_000).optional(),
  })
  .strict();

/** The reader engaged for the first time, for example tapped the first option. */
export const startData = z.object({}).strict();

/**
 * One question answered.
 *
 * Exactly one answer shape is carried. Free text goes to the PII vault and the event holds only a
 * reference, because survey and form answers routinely contain personal data. Numbers and option
 * ids are safe to hold inline.
 */
export const answerData = z
  .object({
    question_id: id('question'),
    question_index: z.number().int().min(0).max(500),
    selected_option_ids: z.array(id('option')).min(1).max(50).optional(),
    value_number: z.number().finite().optional(),
    value_text_ref: id('response').optional(),
    response_ms: z.number().int().min(0).max(86_400_000).optional(),
    /**
     * What the widget showed the reader. Advisory only: scoring is recomputed from the interaction
     * config at projection time, because anything the client asserts can be forged.
     */
    client_marked_correct: z.boolean().optional(),
  })
  .strict();

/** The reader reached the end of the interaction. */
export const completeData = z
  .object({
    duration_ms: z.number().int().min(0).max(86_400_000).optional(),
    /** Client reported, recomputed at projection time for the same reason as above. */
    score: z.number().finite().optional(),
    max_score: z.number().finite().optional(),
    outcome_id: id('outcome').optional(),
  })
  .strict();

/** A form or survey was submitted. Field values live in the PII vault behind this reference. */
export const submitData = z
  .object({
    submission_ref: id('submission'),
    field_count: z.number().int().min(0).max(200),
  })
  .strict();

/**
 * An anonymous device was linked to a known reader.
 *
 * Past events are never rewritten. This event is the link, and projections resolve identity by
 * folding these in, which is what makes retroactive linking possible at all.
 */
export const identifyData = z
  .object({
    identity_ref: id('identity'),
    method: z.enum(['email_hash', 'newsletter_signup', 'publisher_id', 'login']),
  })
  .strict();

export const EVENT_DATA = {
  impression: impressionData,
  start: startData,
  answer: answerData,
  complete: completeData,
  submit: submitData,
  identify: identifyData,
} as const satisfies Record<EventType, z.ZodTypeAny>;
